<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { ChevronsLeft, ChevronsRight, X } from 'lucide-vue-next';
import { NAVIGATION } from '@/app/navigation';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';

defineProps<{ approvalsCount: number; followupsCount: number }>();
const auth = useAuthStore();
const app = useAppStore();
const route = useRoute();

const sections = computed(() =>
  NAVIGATION.map((s) => ({ ...s, items: s.items.filter((i) => !i.anyOf?.length || auth.canAny(...i.anyOf)) })).filter((s) => s.items.length),
);

const matches = (to: string) => (to === '/' ? route.path === '/' : route.path === to || route.path.startsWith(`${to}/`));
/** Highlight only the most specific matching entry (e.g. "Sites" rather than "Projects" on /projects/sites). */
function isActive(to: string) {
  if (!matches(to)) return false;
  const all = NAVIGATION.flatMap((sct) => sct.items.map((i) => i.to));
  return !all.some((other) => other !== to && other.startsWith(to) && other.length > to.length && matches(other));
}
</script>

<template>
  <!-- Mobile overlay -->
  <div v-if="app.mobileNavOpen" class="fixed inset-0 z-40 bg-navy-950/50 lg:hidden" @click="app.mobileNavOpen = false" />
  <aside
    class="fixed inset-y-0 left-0 z-40 flex flex-col bg-navy-800 text-navy-100 transition-all duration-200 dark:bg-[#0b1430] lg:sticky lg:top-0 lg:h-screen lg:translate-x-0"
    :class="[app.sidebarCollapsed ? 'lg:w-[72px]' : 'lg:w-64', app.mobileNavOpen ? 'w-72 translate-x-0' : 'w-72 -translate-x-full']"
    aria-label="Main navigation"
  >
    <div class="flex h-16 items-center gap-3 border-b border-white/10 px-4">
      <img src="/logo-mark.png" alt="" class="h-9 w-9 shrink-0 rounded-lg bg-white p-0.5" />
      <div v-if="!app.sidebarCollapsed || app.mobileNavOpen" class="min-w-0 leading-tight">
        <p class="truncate font-display text-[13px] font-bold tracking-wider text-white">BINGINAPALLI</p>
        <p class="truncate font-display text-[10px] font-semibold tracking-widest text-leaf-400">SOLAR ENERGY</p>
      </div>
      <button type="button" class="ml-auto rounded p-1 text-navy-200 hover:bg-white/10 lg:hidden" aria-label="Close menu" @click="app.mobileNavOpen = false">
        <X class="h-5 w-5" />
      </button>
    </div>

    <nav class="flex-1 space-y-5 overflow-y-auto px-3 py-4">
      <div v-for="(section, i) in sections" :key="i">
        <p v-if="section.label && (!app.sidebarCollapsed || app.mobileNavOpen)" class="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-widest text-navy-300/70">
          {{ section.label }}
        </p>
        <ul class="space-y-0.5">
          <li v-for="item in section.items" :key="item.to">
            <RouterLink
              :to="item.to"
              class="group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition"
              :class="isActive(item.to) ? 'bg-white/10 text-white' : 'text-navy-200 hover:bg-white/5 hover:text-white'"
              :title="app.sidebarCollapsed ? item.label : undefined"
              @click="app.mobileNavOpen = false"
            >
              <component :is="item.icon" class="h-[18px] w-[18px] shrink-0" :class="isActive(item.to) ? 'text-leaf-400' : ''" />
              <span v-if="!app.sidebarCollapsed || app.mobileNavOpen" class="flex-1 truncate">{{ item.label }}</span>
              <span
                v-if="item.badge === 'approvals' && approvalsCount > 0 && (!app.sidebarCollapsed || app.mobileNavOpen)"
                class="rounded-full bg-sun-500 px-1.5 text-[10px] font-bold text-white"
              >{{ approvalsCount }}</span>
              <span
                v-if="item.badge === 'followups' && followupsCount > 0 && (!app.sidebarCollapsed || app.mobileNavOpen)"
                class="rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white"
                title="Overdue or due today"
              >{{ followupsCount }}</span>
              <span
                v-if="item.badge === 'notifications' && app.unreadNotifications > 0 && (!app.sidebarCollapsed || app.mobileNavOpen)"
                class="rounded-full bg-leaf-500 px-1.5 text-[10px] font-bold text-white"
              >{{ app.unreadNotifications }}</span>
            </RouterLink>
          </li>
        </ul>
      </div>
    </nav>

    <button
      type="button"
      class="hidden items-center gap-2 border-t border-white/10 px-5 py-3 text-xs text-navy-300 hover:text-white lg:flex"
      :aria-label="app.sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      @click="app.toggleSidebar()"
    >
      <ChevronsRight v-if="app.sidebarCollapsed" class="h-4 w-4" />
      <template v-else><ChevronsLeft class="h-4 w-4" /> Collapse</template>
    </button>
  </aside>
</template>
